import type { IntlShape } from "react-intl";
import { Path, Shape } from "three";
import { type ZodObject, type ZodTypeAny, z } from "zod";
import type { Input } from "../../../../../../types/shape";
import {
	type ThreeInputLinesType,
	ThreeShapeSideTypes,
	type ThreeSidePathsType,
} from "../../../../../../types/three";
import type { DimensionProperties } from "../../../../../../types/workpiece";
import { formattConstraint } from "../../../../../utils";
import type InputType from "../../../../Input/InputType";
import { JsonInputType } from "../../../../Input/JsonInputType";
import { SideName } from "../../../../Side/SideName";
import Rectangle from "../../Rectangle";

export default class Corner extends Rectangle {
	public static code = "plate_rectangle_rounded_corners";

	public getDimensionsString(input: InputType, intl: IntlShape) {
		const dimensions = super.getDimensionsString(input, intl);
		const { radius_a, radius_b, radius_c, radius_d } = this.formatInputs(input, intl);
		const radii = [
			["A", radius_a],
			["B", radius_b],
			["C", radius_c],
			["D", radius_d],
		];
		const filteredRadii = radii.filter((radius) => radius[1] !== "null" && !!radius[1]);

		if (filteredRadii.length && dimensions) {
			if (radii.every(([_key, value]) => value === radii[0]?.[1])) {
				return intl.formatMessage(
					{
						defaultMessage: "{dimensions}, radius alle hoeken: {value}",
						id: "Z4jikJ",
					},
					{ dimensions, value: radius_a },
				);
			}

			return intl.formatMessage(
				{
					defaultMessage:
						"{dimensions}, radius {cornerCount, plural, one {hoek {corners}} other {hoeken {corners}}}",
					id: "qTM1C0",
				},
				{
					dimensions,
					cornerCount: filteredRadii.length,
					corners: intl.formatList(
						filteredRadii.map(([label, value]) => `${String(label)}: ${String(value)}`),
						{ type: "conjunction" },
					),
				},
			);
		}

		return dimensions;
	}

	public getConstraints(
		input: InputType,
		current: Input,
		equalCorners?: boolean,
	): number | undefined {
		const length = Number(input[JsonInputType.Length] ?? 0);
		const width = Number(input[JsonInputType.Width] ?? 0);

		const radiusA = this.formattConstraint(input[JsonInputType.RadiusA] ?? 0);
		const radiusB = this.formattConstraint(input[JsonInputType.RadiusB] ?? 0);
		const radiusC = this.formattConstraint(input[JsonInputType.RadiusC] ?? 0);
		const radiusD = this.formattConstraint(input[JsonInputType.RadiusD] ?? 0);

		if (equalCorners) {
			return Math.min(width, length) / 2;
		}

		switch (current.id) {
			case JsonInputType.RadiusA: {
				return Math.min(length - radiusB, width - radiusD);
			}
			case JsonInputType.RadiusB: {
				return Math.min(length - radiusA, width - radiusC);
			}
			case JsonInputType.RadiusC: {
				return Math.min(length - radiusD, width - radiusB);
			}
			case JsonInputType.RadiusD: {
				return Math.min(length - radiusC, width - radiusA);
			}
		}

		return super.getConstraints(input, current, equalCorners);
	}

	protected getDimensionSchema(): ZodTypeAny {
		const inputs = super.getDimensionSchema() as ZodObject<Record<string, ZodTypeAny>>;

		return inputs.superRefine((data, context) => {
			const items = [
				{
					key: "radius_a",
					value: data.radius_a,
					max: formattConstraint(Math.min(data.length - data.radius_b, data.width - data.radius_d)),
				},
				{
					key: "radius_b",
					value: data.radius_b,
					max: formattConstraint(Math.min(data.length - data.radius_a, data.width - data.radius_c)),
				},
				{
					key: "radius_c",
					value: data.radius_c,
					max: formattConstraint(Math.min(data.length - data.radius_d, data.width - data.radius_b)),
				},
				{
					key: "radius_d",
					value: data.radius_d,
					max: formattConstraint(Math.min(data.length - data.radius_c, data.width - data.radius_a)),
				},
			];

			const corners = [data.radius_a, data.radius_b, data.radius_c, data.radius_d];
			const equalCorners = Number(corners.reduce((a, b) => (a === b ? a : "")));
			//! this is the closest you can get to checking if it is a combined field, (this fails if all values are the same but switch is not checked)

			if (corners.filter((corner) => corner === 0).length === 4) {
				context.addIssue({
					code: z.ZodIssueCode.custom,
					params: {
						type: "required_corner",
						min: 1,
					},
					path: [JsonInputType.RadiusA],
				});
			}

			if (equalCorners) {
				const max = formattConstraint(Math.min(Number(data.width), Number(data.length)) / 2);

				if (equalCorners > max)
					context.addIssue({
						code: z.ZodIssueCode.too_big,
						type: "number",
						maximum: max,
						inclusive: true,
						path: [JsonInputType.RadiusA],
					});
			} else {
				for (const { key, value, max } of items) {
					if (value > max) {
						context.addIssue({
							code: z.ZodIssueCode.too_big,
							type: "number",
							maximum: max,
							inclusive: true,
							path: [key],
						});
					}
				}
			}
		});
	}

	public calculateSideLength(side: SideName, input: InputType) {
		const [length, width, radiusA, radiusB, radiusC, radiusD] = [
			input[JsonInputType.Length],
			input[JsonInputType.Width],
			input[JsonInputType.RadiusA],
			input[JsonInputType.RadiusB],
			input[JsonInputType.RadiusC],
			input[JsonInputType.RadiusD],
		].map((value) =>
			(typeof value === "string" && value.length > 0) || typeof value === "number"
				? Number(value)
				: Number.NaN,
		);

		if (
			!(
				Number.isNaN(length) ||
				Number.isNaN(width) ||
				Number.isNaN(radiusA) ||
				Number.isNaN(radiusB) ||
				Number.isNaN(radiusC) ||
				Number.isNaN(radiusD)
			)
		) {
			switch (side) {
				case SideName.Length1:
					return Number(length) - Number(radiusA) - Number(radiusB);
				case SideName.Length2:
					return Number(length) - Number(radiusC) - Number(radiusD);
				case SideName.Width1:
					return Number(width) - Number(radiusA) - Number(radiusD);
				case SideName.Width2:
					return Number(width) - Number(radiusB) - Number(radiusC);
				case SideName.CornerA:
					return (Number(radiusA) * Math.PI) / 2;
				case SideName.CornerB:
					return (Number(radiusB) * Math.PI) / 2;
				case SideName.CornerC:
					return (Number(radiusC) * Math.PI) / 2;
				case SideName.CornerD:
					return (Number(radiusD) * Math.PI) / 2;
			}
		}

		return undefined;
	}

	public getThreeShape(props: DimensionProperties["plate_rectangle_rounded_corners"]) {
		const [length, width, radius_a, radius_b, radius_c, radius_d] = [
			props.length / 1000,
			props.width / 1000,
			props.radius_a / 1000,
			props.radius_b / 1000,
			props.radius_c / 1000,
			props.radius_d / 1000,
		];

		const s = new Shape();

		s.moveTo(radius_d, 0);
		s.lineTo(length - radius_c, 0);

		if (radius_c > 0) {
			s.quadraticCurveTo(length, 0, length, radius_c);
		}

		s.lineTo(length, width - radius_b);

		if (radius_b > 0) {
			s.quadraticCurveTo(length, width, length - radius_b, width);
		}

		s.lineTo(radius_a, width);

		if (radius_a > 0) {
			s.quadraticCurveTo(0, width, 0, width - radius_a);
		}

		s.lineTo(0, radius_d);

		if (radius_d > 0) {
			s.quadraticCurveTo(0, 0, radius_d, 0);
		}

		return s;
	}

	public getThreeSidePaths(
		props: DimensionProperties["plate_rectangle_rounded_corners"],
	): ThreeSidePathsType[] {
		const [length, width, radius_a, radius_b, radius_c, radius_d] = [
			props.length / 1000,
			props.width / 1000,
			props.radius_a / 1000,
			props.radius_b / 1000,
			props.radius_c / 1000,
			props.radius_d / 1000,
		];

		return [
			{
				code: "length2",
				type: ThreeShapeSideTypes.side,
				path: new Path().moveTo(radius_d, 0).lineTo(length - radius_c, 0),
				group: "Buitenrand",
			},
			{
				code: "corner-c",
				type: ThreeShapeSideTypes.corner,
				path: new Path()
					.moveTo(length, 0)
					.arc(-radius_c, radius_c, radius_c, -Math.PI / 2, 0, false),
				group: "Buitenrand",
			},
			{
				code: "width2",
				type: ThreeShapeSideTypes.side,
				path: new Path().moveTo(length, radius_c).lineTo(length, width - radius_b),
				group: "Buitenrand",
			},
			{
				code: "corner-b",
				type: ThreeShapeSideTypes.corner,
				path: new Path()
					.moveTo(length, width)
					.arc(-radius_b, -radius_b, radius_b, 0, Math.PI / 2, false),
				group: "Buitenrand",
			},
			{
				code: "length1",
				type: ThreeShapeSideTypes.side,
				path: new Path().moveTo(length - radius_b, width).lineTo(radius_a, width),
				group: "Buitenrand",
			},
			{
				code: "corner-a",
				type: ThreeShapeSideTypes.corner,
				path: new Path()
					.moveTo(0, width)
					.arc(radius_a, -radius_a, radius_a, Math.PI / 2, Math.PI, false),
				group: "Buitenrand",
			},
			{
				code: "width1",
				type: ThreeShapeSideTypes.side,
				path: new Path().moveTo(0, width - radius_a).lineTo(0, radius_d),
				group: "Buitenrand",
			},
			{
				code: "corner-d",
				type: ThreeShapeSideTypes.corner,
				path: new Path()
					.moveTo(0, 0)
					.arc(radius_d, radius_d, radius_d, Math.PI, (3 * Math.PI) / 2, false),
				group: "Buitenrand",
			},
		];
	}

	public getThreeInputLines(
		props: DimensionProperties["plate_rectangle_rounded_corners"],
	): ThreeInputLinesType[] {
		const [length, width, radius_a, radius_b, radius_c, radius_d] = [
			props.length / 1000,
			props.width / 1000,
			props.radius_a / 1000,
			props.radius_b / 1000,
			props.radius_c / 1000,
			props.radius_d / 1000,
		];

		return [
			{
				dimension: props.length,
				code: "length",
				path: new Path().moveTo(0, 0).lineTo(length, 0),
				type: ThreeShapeSideTypes.side,
				doubleOffset: true,
			},
			{
				dimension: props.width,
				code: "width",
				path: new Path().moveTo(0, 0).lineTo(0, width),
				type: ThreeShapeSideTypes.side,
			},
			{
				dimension: props.radius_a,
				code: "radius_a",
				path: new Path().moveTo(0, width).lineTo(radius_a, width),
				type: ThreeShapeSideTypes.corner,
			},
			{
				dimension: props.radius_b,
				code: "radius_b",
				path: new Path().moveTo(length, width).lineTo(length - radius_b, width),
				type: ThreeShapeSideTypes.corner,
			},
			{
				dimension: props.radius_c,
				code: "radius_c",
				path: new Path().moveTo(length, 0).lineTo(length - radius_c, 0),
				type: ThreeShapeSideTypes.corner,
			},
			{
				dimension: props.radius_d,
				code: "radius_d",
				path: new Path().moveTo(radius_d, 0).lineTo(0, 0),
				type: ThreeShapeSideTypes.corner,
			},
		];
	}

	public getDimensions(props: DimensionProperties["plate_rectangle_rounded_corners"]) {
		const hasAnyValue = Object.entries(props)
			.filter(([key]) => key.startsWith("radius_"))
			.some(([_, value]) => !!value);

		return {
			width: props.width ? props.width : 1000,
			length: props.length ? props.length : 2000,
			radius_a: hasAnyValue ? props.radius_a : 100,
			radius_b: hasAnyValue ? props.radius_b : 100,
			radius_c: hasAnyValue ? props.radius_c : 100,
			radius_d: hasAnyValue ? props.radius_d : 100,
		};
	}
}
